import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import SectionContainer from '@/Section/Container'
import CustomImage from '@/UI/Image'
import {
  motion,
  useInView
} from 'framer-motion'
import YouTube from 'react-youtube'
import {
  StyledVideoEmbedContainer,
  StyledVideoEmbedContent,
  StyledVideoEmbedPreview,
  StyledVideoEmbedPreviewButton,
} from './styles'
import Icon from '@/UI/Icon'
import useIsMobile from 'hooks/useIsMobile'

const videoVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
}

const previewVariants = {
  hidden: {
    opacity: 1, zIndex: 1
  },
  visible: {
    opacity: 0, zIndex: -1
  }
}

function VideoEmbed({
  sectionGeneral: { _theme, },
  _sectionHeader,
  sectionContent
}) {
  const ref = useRef(null)
  const player = useRef(null)

  const isInView = useInView(ref)
  const hasThumbnail = sectionContent.thumbnail?.data && sectionContent.thumbnail.data.length > 0
  const hasThumbnailMobile = sectionContent.mobileThumbnail?.data && sectionContent.mobileThumbnail.data.length > 0
  const [status, setStatus] = useState((hasThumbnail || hasThumbnailMobile) ? 'hidden' : 'visible')
  const isMobile = useIsMobile('sm')

  const opts = useMemo(() => ({
    height: '100%',
    width: '100%',
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      controls: 1,
      iv_load_policy: 3,
      rel: 0,
      playlist: (isMobile && !!sectionContent.mobileYoutubeVideoId) ? sectionContent.mobileYoutubeVideoId : sectionContent.youtubeVideoId,
      mute: sectionContent.autoplay ? 1 : 0, // for autoplay to work, the video must be muted
      loop: sectionContent.loop ? 1 : 0,
    },
  }), [sectionContent.youtubeVideoId, isMobile])

  function playVideo() {
    setStatus('visible')
    player.current.playVideo()
  }

  function stopVideo() {
    setStatus((hasThumbnail || hasThumbnailMobile) ? 'hidden' : 'visible')
    player.current.stopVideo()
  }

  function _onVideoReady(event) {
    player.current = event.target
    if (!isInView || !sectionContent.autoplay) return
    playVideo()
  }

  function _onVideoEnd() {
    if (sectionContent.loop) return
    stopVideo()
  }

  function getThumbnail() {
    if (isMobile && hasThumbnailMobile) {
      return sectionContent.mobileThumbnail.data[0]
    }
    return hasThumbnail ? sectionContent.thumbnail.data[0] : null
  }

  useEffect(() => {
    if (!isInView && player.current) {
      stopVideo()
    }

    if (isInView && player.current && sectionContent.autoplay) {
      playVideo()
    }
  }, [isInView, player])

  useEffect(() => {
    player.current = null
    setStatus((hasThumbnail || hasThumbnailMobile) ? 'hidden' : 'visible')
  }, [isMobile])

  return (
    <SectionContainer
      className="VideoEmbedContainer"
      theme="wider"
      smallPaddingY
    >
      <StyledVideoEmbedContainer ref={ref} isMobile={isMobile && !!sectionContent.mobileYoutubeVideoId}>
        {
          hasThumbnail && (
            <StyledVideoEmbedPreview
              as={motion.div}
              initial="hidden"
              variants={previewVariants}
              animate={status}
            >
              <CustomImage data={getThumbnail()} fill />
              <StyledVideoEmbedPreviewButton onClick={() => { playVideo() }}>
                <Icon name="play" />
              </StyledVideoEmbedPreviewButton>
            </StyledVideoEmbedPreview>
          )
        }

        <StyledVideoEmbedContent
          as={motion.div}
          initial="hidden"
          animate={status}
          variants={videoVariants}
        >
          <YouTube
            key={`videoEmbed-${sectionContent.id}-${isMobile ? 'mobile' : 'desktop'}`}
            videoId={(isMobile && sectionContent.mobileYoutubeVideoId) ? sectionContent.mobileYoutubeVideoId : sectionContent.youtubeVideoId}
            opts={opts}
            onReady={_onVideoReady}
            onEnd={_onVideoEnd}
          />
        </StyledVideoEmbedContent>

      </StyledVideoEmbedContainer>

    </SectionContainer>
  )
}

export default VideoEmbed
